import React, {
  useCallback,
  useMemo,
  useEffect,
  useRef,
  useState,
} from "react";
import moment from "moment";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend,
  ReferenceLine,
  ComposedChart,
  LabelList,
} from "recharts";
import { flatten, isNil, values, reject } from "ramda";
import { DateRangePicker } from "src/main_app/components/date_range_picker";
import {
  formatNumber,
  isNilOrEmpty,
  randomColor,
  isNotEmpty,
} from "src/main_app/utils/common";
import { authService } from "../../../../../base/services";
import { saveAs } from "file-saver";
import { useCurrentPng } from "recharts-to-png";
import { useTenantConfig } from "src/main_app/actions/tenant";
import * as htmlToImage from "html-to-image";

const WATER_COST_PER_KL = 3.27; // Cost of water per kL / 1000 litres

const tooltipContentStyle = {
  boxShadow: "0 10px 24px rgba(29,42,68,0.12)",
  borderRadius: "4px",
  border: "none",
  textAlign: "center",
};

const tooltipItemStyle = {
  color: "#05d6f",
  fontSize: "12px",
  fontWeight: "500",
};

const yFormatter = (tickItem) => formatNumber(tickItem) + " ";

const ticksFormatter = (value, index) =>
  moment(value * 1000).format("ddd MMM DD");

const renderTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div
      className="custom-line-tooltip"
      style={{
        padding: "5px",
        backgroundColor: "#fff",
        boxShadow: "0 0 8px 8px rgb(0 0 0 / 7%)",
        borderRadius: "4px",
      }}
    >
      <h5 className="mb-0">{payload[0].payload.displayDate}</h5>
      <br />
      {payload.map((payloadItem, index) => (
        <div
          key={index + ""}
          style={{
            color: payloadItem.stroke,
          }}
        >
          {console.log("payloadItem", payloadItem)}
          {payloadItem.dataKey === "water_cost_usd" ? (
            <>
              {payloadItem.name}: ${formatNumber(payloadItem.value)}
              {payloadItem.unit}
            </>
          ) : (
            <>
              {payloadItem.name}: {formatNumber(payloadItem.value)}{" "}
              {payloadItem.unit}
            </>
          )}
        </div>
      ))}
    </div>
  );
};

const renderTooltip2 = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;

  return (
    <div
      className="custom-line-tooltip"
      style={{
        backgroundColor: "#fff",
        minWidth: "260px",
        boxShadow: "0 0 8px 8px rgb(0 0 0 / 5%)",
        overflow: "auto",
        maxHeight: "410px",
        position: "relative",
        pointerEvents: "auto",
      }}
    >
      <div style={{ padding: "20px 20px 0px 20px" }}>
        <p style={{ fontWeight: "bold", marginBottom: "-2px" }}>
          {payload[0].payload.startDate} - {payload[0].payload.date}
        </p>
        <p style={{ color: "#727f94" }}>
          Total usage: {formatNumber(payload[0].payload.usage)} kL
        </p>
      </div>
      <hr></hr>
      <div style={{ paddingBottom: "15px" }}>
        <table style={{ fontSize: "13px" }}>
          <tr>
            <th style={{ padding: "5px 0px 5px 20px" }}>SERIAL</th>
            <th>USAGE</th>
          </tr>
          {payload[0].payload.merterSerials.map((item, index) => (
            <tr key={index + ""}>
              <td style={{ padding: " 5px 0 5px 20px", color: "#727f94" }}>
                {item}
              </td>
              <td style={{ color: "#727f94" }}>
                {" "}
                {payload[0].payload.consumptions[index] >= 0
                  ? formatNumber(payload[0].payload.consumptions[index])
                  : 0}{" "}
                kL
              </td>
            </tr>
          ))}
        </table>
      </div>
    </div>
  );
};

const renderTooltipQuarterlyUsage = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const data = payload[0].payload;

  return (
    <div
      className="custom-line-tooltip"
      style={{
        backgroundColor: "#fff",
        minWidth: "260px",
        boxShadow: "0 0 8px 8px rgb(0 0 0 / 5%)",
        overflow: "auto",
        maxHeight: "410px",
        position: "relative",
        pointerEvents: "auto",
        borderRadius: "8px",
      }}
    >
      <div style={{ padding: "16px 20px" }}>
        {/* Month or quarter label */}
        <div style={{ padding: "20px 20px 0px 20px" }}>
          <p style={{ fontWeight: "bold", marginBottom: "-2px" }}>
            {payload[0].payload.startDate}
          </p>
          <p style={{ color: "#727f94", marginBottom: "-2px" }}>
            Meter id: {payload[0].payload.merterSerials[0]}
          </p>
          <p style={{ color: "#727f94" }}>
            Total usage: {formatNumber(payload[0].payload.usage)} kL
          </p>
          <p style={{ color: "#727f94" }}>
            Water Cost: ${data.water_cost_usd}/month
          </p>
        </div>
      </div>
    </div>
  );
};

const CustomersUsagePage = ({
  hasRealtimeData,
  dateRangeDataWeekly,
  getDataWeekly,
  dataWeekly,
  costDateRange,
  meterNames,

  dateRangeWaterUsage,
  dateRangeBilling,
  getUsageData,
  usageData,
  getBillingData,
  billingData,
  handleDownloadDataRealTime,
  ticks,

  dateRangeQuarterly,
  getQuarterlyData,
  dataQuarterly,
  costMonthRequest,
  meterNamesQuarterly,
  customer,
  navigateWeeklyPrevious,
  navigateWeeklyNext,
  setCurrentWeeklyDateRange,
  
  // Loading states
  loadingWeekly,
  loadingUsage,
  loadingBilling,
  loadingQuarterly,
}) => {
  const tenantConfig = useTenantConfig();
  const tenantInfo = authService.getTenantInfo();
  const isHwCommercial = tenantInfo?.id === 3;
  const checkHasRealtimeData = [];
  if (hasRealtimeData) {
    for (const itemDataWeekly of dataWeekly) {
      if (itemDataWeekly["date"] != undefined) {
        checkHasRealtimeData.push(itemDataWeekly);
        break;
      }
    }
  }

  const transformedDataWeekly = useMemo(() => {
    if (!isHwCommercial || !hasRealtimeData || isNilOrEmpty(dataWeekly)) {
      return dataWeekly;
    }

    // Convert interval volume (L/30min) to average flow (L/min)
    // by dividing by 30 for each meter series value.
    return dataWeekly.map((item) => {
      const nextItem = { ...item };
      meterNames.forEach((meterName) => {
        const value = nextItem[meterName];
        if (typeof value === "number" && !Number.isNaN(value)) {
          const flowPerMinute = value / 30;
          // round to 4 decimal places
          nextItem[meterName] =
            Math.round(flowPerMinute * 10000) / 10000;
        }
      });
      return nextItem;
    });
  }, [dataWeekly, meterNames, hasRealtimeData, isHwCommercial]);

  const allSumData = useMemo(() => {
    const dataUsage = [];
    if (!hasRealtimeData) return dataUsage;

    const source = transformedDataWeekly || dataWeekly;

    for (const itemDataWeekly of source) {
      const { date, displayDate, water_cost_usd, ...dataSumUsage } =
        itemDataWeekly;
      dataUsage.push(values(dataSumUsage));
    }
    return reject(isNil, flatten(dataUsage));
  }, [transformedDataWeekly, dataWeekly, hasRealtimeData]);

  const totalUsageInPeriodKL = useMemo(() => {
    if (!costDateRange || !costDateRange.length) return 0;
    return costDateRange.reduce(
      (sum, row) => sum + (row.total_consumption_kl ?? 0),
      0
    );
  }, [costDateRange]);

  const totalCostInPeriod = useMemo(
    () => totalUsageInPeriodKL * WATER_COST_PER_KL,
    [totalUsageInPeriodKL]
  );

  const dataWeeklyMax = useMemo(
    () => Math.ceil(Math.max(...allSumData) * 10) / 10,
    [allSumData]
  );

  const dataWaterCostMax = useMemo(
    () => Math.ceil(Math.max(...allSumData) * 10) / 10,
    [allSumData]
  );

  const allDataQuarterly = useMemo(() => {
    const dataUsage = [];

    for (const itemDataQuarterly of dataQuarterly) {
      const { date, displayDate, ...dataSumUsage } = itemDataQuarterly;
      dataUsage.push(values(dataSumUsage));
    }
    return reject(isNil, flatten(dataUsage));
  }, [dataQuarterly, hasRealtimeData]);

  const dataQuarterlyMax = useMemo(
    () => Math.ceil(Math.max(...allDataQuarterly) * 10) / 10,
    [allDataQuarterly]
  );

  const onApplyWeeklyConsumption = useCallback(
    (event, picker) => {
      const startDate = picker.startDate.utcOffset(0, true).toDate();
      const endDate = picker.endDate.utcOffset(0, true).toDate();
      
      // Update the current date range state
      setCurrentWeeklyDateRange && setCurrentWeeklyDateRange([startDate, endDate]);
      
      getDataWeekly && getDataWeekly(startDate, endDate);
    },
    [getDataWeekly, setCurrentWeeklyDateRange]
  );

  const onApplyWaterUsage = useCallback(
    (event, picker) => {
      getUsageData &&
        getUsageData(
          picker.startDate.utcOffset(0, true).toDate(),
          picker.endDate.utcOffset(0, true).toDate()
        );
    },
    [getUsageData]
  );

  const onApplyBillingUsage = useCallback(
    (event, picker) => {
      getBillingData &&
        getBillingData(
          picker.startDate.utcOffset(0, true).toDate(),
          picker.endDate.utcOffset(0, true).toDate()
        );
    },
    [getBillingData]
  );

  const onApplyQuarterlyUsage = useCallback(
    (event, picker) => {
      getQuarterlyData &&
        getQuarterlyData(
          picker.startDate.utcOffset(0, true).toDate(),
          picker.endDate.utcOffset(0, true).toDate()
        );
    },
    [getQuarterlyData]
  );

  const yLabel = useMemo(
    () => ({
      value: "Usage (kL)",
      angle: -90,
      position: "top",
      dy: 170,
      dx: -15,
    }),
    []
  );

  const yLabelSUMs = useMemo(
    () => ({
      value: isHwCommercial ? "L/min" : "L/30 min",
      angle: -90,
      position: "top",
      dy: 170,
      dx: -10,
    }),
    [isHwCommercial]
  );

  const user = authService.getUser();

  useEffect(() => {
    import("./style.scss");
  }, []);

  //Donwload image chart
  // //Real-time chart
  // const [getRealTimeChartPng, { ref: realTimeChartRef, isLoading}] = useCurrentPng();
  // const handleRealTimeDownload = useCallback(async () => {
  //   const png = await getRealTimeChartPng();
  //   if (png) {
  //     saveAs(png, "real-time-weekly-consumption-chart.png");
  //   }
  // }, [getRealTimeChartPng]);

  //Real-time chart
  const handleRealTimeDownload = () => {
    htmlToImage
      .toPng(document.getElementById("realtime-chart"))
      .then(function (dataUrl) {
        saveAs(dataUrl, "real-time-weekly-consumption-chart.png");
      });
  };
  //Usage chart
  const handleMeteredDownload = () => {
    htmlToImage
      .toPng(document.getElementById("metered-chart"))
      .then(function (dataUrl) {
        saveAs(dataUrl, "metered-consumption-chart.png");
      });
  };
  //Billing chart
  const handleBillingDownload = () => {
    htmlToImage
      .toPng(document.getElementById("billing-chart"))
      .then(function (dataUrl) {
        saveAs(dataUrl, "billing-consumption-chart.png");
      });
  };

  const handleQuarterlyDownload = () => {
    htmlToImage
      .toPng(document.getElementById("quarterly-usage-chart"))
      .then(function (dataUrl) {
        saveAs(dataUrl, "quarterly-usage-chart.png");
      });
  };

  const refLineY = dataWeeklyMax * 0.8;
  const [aspect, setAspect] = useState(getAspect(window.innerWidth));

  useEffect(() => {
    const handleResize = () => setAspect(getAspect(window.innerWidth));
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  function getAspect(width) {
    if (width < 480) return 10 / 7; // taller for mobile
    if (width < 768) return 10 / 5; // tablet
    return 10 / 3; // desktop
  }

  return (
    <>
      {hasRealtimeData && (
        <div className="container-fluid content">
          <h3 className="sub-title">
            {tenantConfig?.isWeeklyUsageTitle
              ? "Weekly Usage"
              : "Weekly Consumption"}
          </h3>
          {/* only show this part if tenant is Residential which mean tenant id = 6 */}
            <div
              className="mb-4"
              style={{ fontSize: "18px", fontWeight: 600, display: "flex", flexWrap: "wrap" }}
            >
              <span>
                Total usage in period:{" "}
                <span style={{ color: "#09A3B3" }}>
                  {Number(totalUsageInPeriodKL).toFixed(4)} kL
                </span>
              </span>
              {(tenantInfo.id === 6 || tenantInfo.id === 3)&& 
                <>
                  <span style={{ margin: "0 8px" }}>·</span>
                  <span>
                    Cost:{" "}
                    <span style={{ color: "#09A3B3" }}>
                      ${Number(totalCostInPeriod).toFixed(2)}
                    </span>
                  </span>
                </>
              }
            </div>
          <div className="form-group">
            <label>Date range</label>
            <div className="d-flex" style={{ alignItems: 'flex-start' }}>
              <button 
                className="btn btn-outline-secondary btn-sm mr-2"
                onClick={navigateWeeklyPrevious}
                title="Previous period"
                style={{ 
                  minWidth: '40px', 
                  height: '38px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <i className="fas fa-chevron-left"></i>
              </button>
              <div style={{ flex: 1, position: 'relative' }}>

                <DateRangePicker
                  key={`${dateRangeDataWeekly[0].getTime()}-${dateRangeDataWeekly[1].getTime()}`}
                  setting={{
                    startDate: dateRangeDataWeekly[0],
                    endDate: dateRangeDataWeekly[1],
                  }}
                  onApply={onApplyWeeklyConsumption}
                >
                  <input type="text" className="form-control" />
                </DateRangePicker>
              </div>
              <button 
                className="btn btn-outline-secondary btn-sm ml-2"
                onClick={navigateWeeklyNext}
                title="Next period"
                style={{ 
                  minWidth: '40px', 
                  height: '38px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          </div>
          {/* { isNilOrEmpty(dataWeekly) ? ( */}
          {isNilOrEmpty(checkHasRealtimeData) ? (
            <div className="no-item-message">
              {" "}
              There is currently no usage data available.
            </div>
          ) : (
            <>
              <div>
                <a
                  className="btn btn-primary btn-sm"
                  onClick={handleRealTimeDownload}
                >
                  Download as image
                </a>
                <a
                  className="btn btn-primary btn-sm ml-2"
                  onClick={handleDownloadDataRealTime}
                >
                  Download data
                </a>
              </div>
              <div className="card" id="realtime-chart">
                <div className="card-body" style={{ position: 'relative' }}>
                  {loadingWeekly && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(255, 255, 255, 0.8)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 10
                    }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <div className="spinner-border text-primary" role="status">
                          <span className="sr-only">Loading...</span>
                        </div>
                        <small className="mt-2 text-muted">Loading weekly data...</small>
                      </div>
                    </div>
                  )}
                  <ResponsiveContainer width="100%" aspect={aspect}>
                    <LineChart
                      height={180}
                      data={isHwCommercial ? transformedDataWeekly : dataWeekly}
                      margin={{
                        top: 20,
                        right: 40,
                        left: -15,
                        bottom: 5,
                      }}
                    >
                      <CartesianGrid stroke="#e9ebf1" />
                      <XAxis
                        dataKey="date"
                        stroke="#e9ebf1"
                        type="category"
                        interval="preserveStartEnd"
                        ticks={ticks}
                        tickFormatter={ticksFormatter}
                      />

                      {/* Left axis: Usage */}
                      <YAxis
                        stroke="#e9ebf1"
                        domain={[0, dataWeeklyMax]}
                        label={yLabelSUMs}
                      />

                      {/* Right axis: Cost */}
                      {/* <YAxis
                        yAxisId="right"
                        orientation="right"
                        domain={[0, dataWaterCostMax]}
                        stroke="#FF8042"
                        label={{
                          value: "Monthly cost ($)",
                          angle: -90,
                          dy: -40,
                          position: "insideRight",
                        }}
                      /> */}

                      {/* Usage lines */}
                      {meterNames.map((meterName, index) => (
                        <Line
                          key={index}
                          type="monotone"
                          dataKey={meterName}
                          name={
                            tenantConfig.isConcatMeterAddress && customer
                              ? meterName +
                                " - " +
                                customer.address +
                                `, ${customer.suburb_name}`
                              : meterName
                          }
                          stroke={
                            index === 0
                              ? customer?.value?.use_recycled_water
                                ? "#C8A2C8"
                                : "#09A3B3"
                              : randomColor(index)
                          }
                          dot={false}
                          unit=" L"
                        />
                      ))}

                      {/* Cost line */}
                      {/* <Line
                        yAxisId="right"
                        dataKey="water_cost_usd"
                        type="monotone"
                        stroke="#FF8042"
                        strokeWidth={2}
                        dot={{ r: 3 }}
                        activeDot={{ r: 5 }}
                        name="Water Cost"
                        connectNulls={true}
                        strokeDasharray="3 5"
                        unit="/month"
                      /> */}

                      <Tooltip
                        contentStyle={tooltipContentStyle}
                        itemStyle={tooltipItemStyle}
                        content={renderTooltip}
                      />
                      <Legend iconType="circle" margin={{ top: 50 }} />
                    </LineChart>
                  </ResponsiveContainer>
                  {customer?.value?.use_recycled_water && (
                    <div className="mt-2 text-center" style={{ fontSize: 12 }}>
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          marginRight: 12,
                        }}
                      >
                        <span
                          style={{
                            width: 20,
                            height: 3,
                            backgroundColor: "#C8A2C8",
                            display: "inline-block",
                            marginRight: 6,
                          }}
                        />
                        Recycled meter
                      </span>
                    </div>
                  )}

                  {/* <ResponsiveContainer width="100%" aspect={aspect}>
                    <LineChart
                      height={180}
                      data={dataWeekly}
                      margin={{
                        top: 20,
                        right: 5,
                        left: -15,
                        bottom: 5,
                      }}
                      barGap={5}
                      barCategoryGap={5}
                    >
                      <CartesianGrid stroke="#e9ebf1" />
                      <XAxis
                        dataKey="date"
                        stroke="#e9ebf1"
                        type={"category"}
                        interval={"preserveStartEnd"}
                        ticks={ticks}
                        tickFormatter={ticksFormatter}
                      />
                      <YAxis
                        stroke="#e9ebf1"
                        domain={[0, dataWeeklyMax]}
                        label={yLabelSUMs}
                      />
                      {meterNames.map((meterName, index) => (
                        <Line
                          key={index + ""}
                          type="monotone"
                          dataKey={meterName}
                          name={
                            tenantConfig.isConcatMeterAddress && customer
                              ? meterName +
                                " - " +
                                customer.address +
                                `, ${customer.suburb_name}`
                              : meterName
                          }
                          stroke={index === 0 ? "#09A3B3" : randomColor(index)}
                          dot={false}
                          unit={" L"}
                        />
                      ))}
                      <Tooltip
                        contentStyle={tooltipContentStyle}
                        itemStyle={tooltipItemStyle}
                        content={renderTooltip}
                      />
                      <Legend iconType={"circle"} margin={{ top: 50 }} />
                    </LineChart>
                  </ResponsiveContainer> */}
                </div>
              </div>
            </>
          )}
        </div>
      )}
      {!tenantConfig.isDisabledMeteredChart && (
        <>
          {user.role != "Customer" && (
            <div className="container-fluid content">
              <h3 className="sub-title">Metered Consumption</h3>
              <div className="form-group">
                <label>Date range</label>
                <DateRangePicker
                  setting={{
                    startDate: dateRangeWaterUsage[0],
                    endDate: dateRangeWaterUsage[1],
                  }}
                  onApply={onApplyWaterUsage}
                >
                  <input type="text" className="form-control" />
                </DateRangePicker>
              </div>
              {isNilOrEmpty(usageData) ? (
                <div className="no-item-message">
                  {" "}
                  There is currently no usage data available.
                </div>
              ) : (
                <>
                  <a
                    className="btn btn-primary btn-sm"
                    onClick={handleMeteredDownload}
                  >
                    Download as image
                  </a>
                  <div className="card" id="metered-chart">
                    <div className="card-body" style={{ position: 'relative' }}>
                      {loadingUsage && (
                        <div style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundColor: 'rgba(255, 255, 255, 0.8)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          zIndex: 10
                        }}>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <div className="spinner-border text-primary" role="status">
                              <span className="sr-only">Loading...</span>
                            </div>
                            <small className="mt-2 text-muted">Loading usage data...</small>
                          </div>
                        </div>
                      )}
                      <ResponsiveContainer width="100%" aspect={aspect}>
                        <BarChart
                          data={usageData}
                          margin={{
                            top: 20,
                            right: 5,
                            left: -15,
                            bottom: 5,
                          }}
                        >
                          <CartesianGrid stroke="#e9ebf1" />
                          <XAxis
                            dataKey="date"
                            stroke="#e9ebf1"
                            type={"category"}
                          />
                          <YAxis
                            dataKey={"usage"}
                            stroke="#e9ebf1"
                            tickFormatter={yFormatter}
                            label={yLabel}
                          />
                          <Tooltip
                            contentStyle={{
                              boxShadow: "0 10px 24px rgba(29,42,68,0.12)",
                              borderRadius: "4px",
                              border: "none",
                              pointerEvents: "auto",
                            }}
                            itemStyle={{
                              color: "#05d6f",
                              fontSize: "12px",
                              fontWeight: "500",
                            }}
                            content={renderTooltip2}
                          />
                          <Bar
                            dataKey={"usage"}
                            fill="#09A3B3"
                            radius={[5, 5, 0, 0]}
                            barSize={24}
                            unit=" ML"
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </>
      )}

      {/* start-billing-graph */}
      {/* {user.role != "Customer" && */}
      {!tenantConfig.isDisabledBillingChart && (
        <>
          <div className="container-fluid content">
            <h3 className="sub-title">Billing Consumption</h3>
            <div className="form-group">
              <label>Date range</label>
              <DateRangePicker
                setting={{
                  startDate: dateRangeBilling[0],
                  endDate: dateRangeBilling[1],
                }}
                onApply={onApplyBillingUsage}
              >
                <input type="text" className="form-control" />
              </DateRangePicker>
            </div>
            {isNilOrEmpty(billingData) ? (
              <div className="no-item-message">
                {" "}
                There is currently no usage data available.
              </div>
            ) : (
              <>
                <a
                  className="btn btn-primary btn-sm"
                  onClick={handleBillingDownload}
                >
                  Download as image
                </a>
                <div className="card" id="billing-chart">
                  <div className="card-body" style={{ position: 'relative' }}>
                    {loadingBilling && (
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 10
                      }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <div className="spinner-border text-primary" role="status">
                            <span className="sr-only">Loading...</span>
                          </div>
                          <small className="mt-2 text-muted">Loading billing data...</small>
                        </div>
                      </div>
                    )}
                    <ResponsiveContainer width="100%" aspect={aspect}>
                      <BarChart
                        data={billingData}
                        margin={{
                          top: 20,
                          right: 5,
                          left: -15,
                          bottom: 5,
                        }}
                      >
                        <CartesianGrid stroke="#e9ebf1" />
                        <XAxis
                          dataKey="date"
                          stroke="#e9ebf1"
                          type={"category"}
                        />
                        <YAxis
                          dataKey={"usage"}
                          stroke="#e9ebf1"
                          tickFormatter={yFormatter}
                          label={yLabel}
                        />
                        <Tooltip
                          contentStyle={{
                            boxShadow: "0 10px 24px rgba(29,42,68,0.12)",
                            borderRadius: "4px",
                            border: "none",
                            pointerEvents: "auto",
                          }}
                          itemStyle={{
                            color: "#05d6f",
                            fontSize: "12px",
                            fontWeight: "500",
                          }}
                          content={renderTooltip2}
                        />
                        <Bar
                          dataKey={"usage"}
                          fill="#09A3B3"
                          radius={[5, 5, 0, 0]}
                          barSize={24}
                          unit=" ML"
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      )}
      {/* } */}
      {/* end-billing-graph */}

      {/* Start Quarterly usage */}
      {tenantConfig.isEnabledQuarterlyChart && (
        <div className="container-fluid content">
          <h3 className="sub-title">Quarterly usage</h3>
          <div className="form-group">
            <label>Date range</label>
            <DateRangePicker
              setting={{
                startDate: dateRangeQuarterly[0],
                endDate: dateRangeQuarterly[1],
              }}
              onApply={onApplyQuarterlyUsage}
            >
              <input type="text" className="form-control" />
            </DateRangePicker>
          </div>
          {isNilOrEmpty(dataQuarterly) ? (
            <div className="no-item-message">
              {" "}
              There is currently no usage data available.
            </div>
          ) : (
            <>
              <a
                className="btn btn-primary btn-sm"
                onClick={handleQuarterlyDownload}
              >
                Download as image
              </a>
              <div className="card" id="quarterly-usage-chart">
                <div className="card-body" style={{ position: 'relative' }}>
                  {loadingQuarterly && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(255, 255, 255, 0.8)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 10
                    }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <div className="spinner-border text-primary" role="status">
                          <span className="sr-only">Loading...</span>
                        </div>
                        <small className="mt-2 text-muted">Loading quarterly data...</small>
                      </div>
                    </div>
                  )}
                  {/* <ResponsiveContainer width="100%" aspect={aspect}>
                    <BarChart
                      data={dataQuarterly}
                      margin={{
                        top: 20,
                        right: 5,
                        left: -15,
                        bottom: 5,
                      }}
                    >
                      <CartesianGrid stroke="#e9ebf1" />
                      <XAxis
                        dataKey="date"
                        stroke="#e9ebf1"
                        type={"category"}
                      />
                      <YAxis
                        dataKey={"usage"}
                        stroke="#e9ebf1"
                        tickFormatter={yFormatter}
                        label={yLabel}
                      />
                      <Tooltip
                        contentStyle={{
                          boxShadow: "0 10px 24px rgba(29,42,68,0.12)",
                          borderRadius: "4px",
                          border: "none",
                          pointerEvents: "auto",
                        }}
                        itemStyle={{
                          color: "#05d6f",
                          fontSize: "12px",
                          fontWeight: "500",
                        }}
                        content={renderTooltipQuarterlyUsage}
                      />
                      <Bar
                        dataKey={"usage"}
                        fill="#09A3B3"
                        radius={[5, 5, 0, 0]}
                        barSize={24}
                        unit=" ML"
                      />
                    </BarChart>
                  </ResponsiveContainer> */}
                  <ResponsiveContainer width="100%" aspect={aspect}>
                    <ComposedChart
                      data={dataQuarterly}
                      margin={{ top: 20, right: 5, left: -15, bottom: 5 }}
                    >
                      <CartesianGrid stroke="#e9ebf1" />
                      <XAxis dataKey="date" stroke="#e9ebf1" type="category" />

                      {/* Left axis for usage */}
                      <YAxis
                        yAxisId="left"
                        dataKey="usage"
                        stroke="#e9ebf1"
                        tickFormatter={yFormatter}
                        label={yLabel}
                      />

                      {/* Right axis for cost */}
                      {/* <YAxis
                        yAxisId="right"
                        dataKey="water_cost_usd"
                        orientation="right"
                        stroke="#FF8042"
                        label={{
                          value: "Monthly cost ($)",
                          angle: -90,
                          dy: -40,
                          dx: -15,
                          position: "insideRight",
                        }}
                      /> */}

                      <Tooltip
                        contentStyle={{
                          boxShadow: "0 10px 24px rgba(29,42,68,0.12)",
                          borderRadius: "4px",
                          border: "none",
                          pointerEvents: "auto",
                        }}
                        itemStyle={{
                          color: "#05d6f",
                          fontSize: "12px",
                          fontWeight: "500",
                        }}
                        content={renderTooltipQuarterlyUsage}
                      />

                      {/* Bar — usage */}
                      <Bar
                        yAxisId="left"
                        dataKey="usage"
                        fill="#09A3B3"
                        radius={[5, 5, 0, 0]}
                        barSize={24}
                        unit=" kL"
                        name="Total Usage (kL)"
                      />

                      {/* Dotted Line — cost */}
                      {/* <Line
                        yAxisId="right"
                        dataKey="water_cost_usd"
                        type="monotone"
                        stroke="#FF8042"
                        strokeWidth={2}
                        dot={{ r: 3 }}
                        activeDot={{ r: 5 }}
                        name="Monthly cost ($)"
                        connectNulls={true}
                      /> */}

                      {/* <Line
                        yAxisId="right"
                        dataKey="water_cost_usd"
                        type="monotone"
                        stroke="#FF8042"
                        strokeWidth={2}
                        dot={{ r: 3 }}
                        activeDot={{ r: 5 }}
                        name="Monthly cost ($)"
                        connectNulls={true}
                      >
                        <LabelList
                          dataKey="water_cost_usd"
                          position="top"
                          formatter={(value) => `$${value}`} // optional formatting
                          style={{
                            fontSize: 11,
                            fill: "#FF8042",
                            fontWeight: 500,
                          }}
                        />
                      </Line> */}
                      {/* <Line
                        yAxisId="right"
                        dataKey="water_cost_usd"
                        type="monotone"
                        stroke="transparent" // hides the line
                        strokeWidth={0} // optional: ensures no visible line
                        dot={{ r: 4, fill: "#FF8042" }} // visible orange dots
                        activeDot={{ r: 6, fill: "#FF8042" }}
                        name="Monthly cost ($)"
                        connectNulls={true}
                      >
                        <LabelList
                          dataKey="water_cost_usd"
                          position="top"
                          formatter={(value) => `$${value}`}
                          style={{
                            fontSize: 11,
                            fill: "#FF8042",
                            fontWeight: 500,
                          }}
                        />
                      </Line> */}
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </>
          )}
        </div>
      )}
      {/* End Quarterly usage */}
    </>
  );
};

export default CustomersUsagePage;
